/**
 * The two colour ramps ENHANCED EFFECTS paints its particles with, taken from
 * the original's own art rather than invented.
 *
 * ## Where these numbers come from — a census, not a guess
 *
 * `EXPLO.BMP` and `SMOKE.BMP` are the only two sheets a burning aeroplane is
 * ever drawn from, and both are baked against `BARON.PAL`
 * (`packages/baker/src/palette-match.ts`'s `SPRITE_SHEET_PALETTE`). The bake
 * resolves palette indices to RGBA and throws the indices away, so the ramps
 * below were recovered by histogramming the baked sheets against
 * `data/baked/palettes/BARON.PAL.json` — every opaque pixel of every frame,
 * matched back to the entry it came from:
 *
 * - **`EXPLO.BMP` uses exactly fifteen entries, `BARON.PAL` 209-223, and
 *   nothing else.** They are contiguous and they run hot to cold in index
 *   order: `#ffffe7` white-yellow, `#fbfb6d`, `#fbfb00` yellow, `#f7c300`,
 *   `#f38e00`, `#ef5d00` orange, `#eb2800`, then eight rungs of red darkening
 *   to `#750000`. Entry 208 before it is black and 224 after it starts an
 *   unrelated flesh ramp, so the run is bounded on both sides by the palette
 *   itself — this is the fire ramp Dynamix drew the fireball with, whole.
 * - **`SMOKE.BMP` uses exactly eleven, 64-74**, `#141414` to `#696969`, which
 *   `smoke-trail.ts` had already recorded from the other direction. Confirmed
 *   here by the same census.
 *
 * `fire-palette.test.ts` re-runs both censuses against the real baked files, so
 * these tables cannot drift from the art.
 *
 * ## Why a ramp is *quantised* and never interpolated
 *
 * A particle picks the nearest rung and takes it exactly. Blending two rungs
 * would produce colours that are not in `BARON.PAL` at all, which is the one
 * thing that would make a modern particle read as not-this-game — the whole
 * picture around it is 256 fixed entries. Fifteen rungs over a 0.45 s flame is
 * finer than the eye resolves anyway, and the ordered-dither stipple every
 * particle is cut with (`three/screen-sprites.ts`) does the rest of the
 * softening the way the hardware of the day would have.
 */

/** A ramp as the GPU wants it: rungs of three 0..1 floats, hot end first. */
export type ColorRamp = Float32Array;

/** Components per rung. */
const RGB = 3;

function rampOf(entries: readonly (readonly [number, number, number])[]): ColorRamp {
  const ramp = new Float32Array(entries.length * RGB);
  entries.forEach(([r, g, b], rung) => {
    ramp[rung * RGB] = r / 255;
    ramp[rung * RGB + 1] = g / 255;
    ramp[rung * RGB + 2] = b / 255;
  });
  return ramp;
}

/** `BARON.PAL`'s first fire entry — see the census above. */
export const FIRE_RAMP_FIRST_INDEX = 209;
/** `BARON.PAL`'s first smoke entry, the one `smoke-trail.ts` names. */
export const SMOKE_RAMP_FIRST_INDEX = 64;

/** `BARON.PAL` 209-223, hot to cold: every colour `EXPLO.BMP` is drawn in. */
export const FIRE_RAMP: ColorRamp = rampOf([
  [255, 255, 231],
  [251, 251, 109],
  [251, 251, 0],
  [247, 195, 0],
  [243, 142, 0],
  [239, 93, 0],
  [235, 40, 0],
  [231, 0, 0],
  [215, 0, 0],
  [199, 0, 0],
  [182, 0, 0],
  [166, 0, 0],
  [150, 0, 0],
  [134, 0, 0],
  [117, 0, 0],
]);

/**
 * `BARON.PAL` 64-74, light to dark — the reverse of the fire ramp's direction,
 * because a puff *darkens* as it thins is wrong and it *lightens* as it thins
 * is what a real trail does: the dense head is near-black and the dissipating
 * tail goes pale grey. So rung 0 is the oldest end here and the youngest end
 * there, which is why nothing shares a sampling helper's `t` convention —
 * `smokeRampAt` and `fireRampAt` each say which way round they run.
 */
export const SMOKE_RAMP: ColorRamp = rampOf([
  [20, 20, 20],
  [28, 28, 28],
  [36, 36, 36],
  [44, 44, 44],
  [52, 52, 52],
  [60, 60, 60],
  [73, 73, 73],
  [81, 81, 81],
  [89, 89, 89],
  [97, 97, 97],
  [105, 105, 105],
]);

/** Where a sampled colour is written, so sampling never allocates. */
export interface MutableRgb {
  r: number;
  g: number;
  b: number;
}

export function emptyRgb(): MutableRgb {
  return { r: 0, g: 0, b: 0 };
}

/** How many rungs a ramp has. */
export function rampRungs(ramp: ColorRamp): number {
  return ramp.length / RGB;
}

/**
 * Reads rung `Math.round(t · (rungs - 1))` of `ramp` into `out`, clamped at
 * both ends. Quantised on purpose — see the header.
 */
export function rampAt(ramp: ColorRamp, t: number, out: MutableRgb): MutableRgb {
  const rungs = rampRungs(ramp);
  const rung = Math.min(rungs - 1, Math.max(0, Math.round(t * (rungs - 1))));
  out.r = ramp[rung * RGB] ?? 0;
  out.g = ramp[rung * RGB + 1] ?? 0;
  out.b = ramp[rung * RGB + 2] ?? 0;
  return out;
}

/**
 * A flame's colour at `age / life`: 0 is the white-yellow core it is born at,
 * 1 the dark red it dies at. Exactly the direction `EXPLO.BMP`'s own three
 * six-frame sequences run (`sprite-effects.ts`'s `SUSTAINED_FIRE_STATES` note),
 * so a particle flame cools through the same colours the sprite fireball does.
 */
export function fireRampAt(lifeFraction: number, out: MutableRgb): MutableRgb {
  return rampAt(FIRE_RAMP, coolingCurve(lifeFraction), out);
}

/**
 * How far down the ramp a flame of age `t` has fallen.
 *
 * Not `t`. A steady emitter has as many particles in every age band, so a
 * linearly-cooling flame spends a fifteenth of its mass on each rung — and
 * since the two hottest rungs are near-white, that is a *lot* of white. On a
 * real GPU at 1280x800 it read as a headlight bolted to the fuselage rather
 * than as fire (2026-08-23, first capture pass).
 *
 * `t^0.7` moves the mass down the ramp without overshooting: a quarter of the
 * way through its life a flame is already at `EXPLO.BMP`'s oranges, so the
 * white-yellow core is the small bright heart it should be, while the deep reds
 * stay where they belong — the dying tip. (`√t` was the first attempt and
 * overcorrected: it read as a red flare rather than a flame, because it puts
 * *half* the mass past the orange rungs.) It is also the more faithful curve —
 * the original's own six-frame sequence spends four frames past the
 * yellow-white bloom.
 */
const COOLING_EXPONENT = 0.7;

function coolingCurve(lifeFraction: number): number {
  return Math.min(1, Math.max(0, lifeFraction)) ** COOLING_EXPONENT;
}

/**
 * A puff's colour at `age / life`: 0 is the near-black `#141414` it leaves the
 * airframe as, 1 the pale `#696969` it thins to. See `SMOKE_RAMP` for why this
 * runs the opposite way to the fire ramp.
 */
export function smokeRampAt(lifeFraction: number, out: MutableRgb): MutableRgb {
  return rampAt(SMOKE_RAMP, lifeFraction, out);
}
